import type { LocationQuery, LocationQueryRaw, RouteLocationRaw, RouteRecordName, RouteRecordRaw } from 'vue-router'

/**
 * The pre-phase-6 Engage pages, by route name, and where they live now: the per-channel campaign
 * lists and inboxes became one Campaigns page and one Inbox, each with a `channel` filter. Old
 * links, bookmarks and notifications keep working through these redirect records (R23).
 *
 * Keyed on the record's name, not the URL: the router matches paths case-insensitively, so
 * `/WhatsApp/Inbox` lands on the `WaInbox` record with that spelling in `to.path`.
 */
const LEGACY_ENGAGE = {
  OutreachCampaigns: { from: 'outreach', path: '/campaigns', channel: 'email' },
  OutreachInbox: { from: 'outreach/inbox', path: '/inbox', channel: 'email' },
  WaCampaigns: { from: 'whatsapp', path: '/campaigns', channel: 'whatsapp' },
  WaInbox: { from: 'whatsapp/inbox', path: '/inbox', channel: 'whatsapp' },
} as const

type LegacyEngageName = keyof typeof LEGACY_ENGAGE

const isLegacyEngageName = (name: unknown): name is LegacyEngageName =>
  typeof name === 'string' && Object.prototype.hasOwnProperty.call(LEGACY_ENGAGE, name)

/**
 * Where a legacy Engage route goes, or null when `name` is not one of them. The legacy page picks
 * the channel; every other param is kept as it was: `org`, the inbox's `contactId` filter and
 * `enrollment` (the inbox model opens `thread=em:<enrollment>` from it).
 */
export function legacyEngageRedirect(
  name: RouteRecordName | null | undefined,
  query: LocationQuery | LocationQueryRaw,
): RouteLocationRaw | null {
  if (!isLegacyEngageName(name)) return null
  const target = LEGACY_ENGAGE[name]
  const { channel: _staleChannel, ...rest } = query
  return { path: target.path, query: { channel: target.channel, ...rest } }
}

/**
 * The four redirect records, children of the app layout route (`router/index.ts`). They keep the
 * old names so named links resolve, and run no guards of their own: the target's `engageGuard`
 * decides who may open it.
 */
export const legacyEngageRoutes: RouteRecordRaw[] = (Object.keys(LEGACY_ENGAGE) as LegacyEngageName[]).map((name) => ({
  path: LEGACY_ENGAGE[name].from,
  name,
  redirect: (to) => legacyEngageRedirect(to.name, to.query) ?? '/dashboard',
}))
